import { notification } from "antd";
import axios from "axios";
import { Auth } from "./Auth";
import { LanguageService } from "./LanguageService";
import { CustomErrors } from "../constants/Errors/CustomErrors";
import { ErrorType } from "../constants/Errors/ErrorTypes";
import { Utils } from "./Utils";
import { refreshToken } from "../actions/UserActions";
import history from "./History";
import { Browser } from "./helpers/Browser";
import RolesHelper from "./permisions/RolesHelper";
import { getAppPathname, getAppUrl } from "./AppBasePath";
import { RoutePath } from "../constants/SitePaths";

/**
 * Module-local record of the single in-flight PROJECT_NOT_FOUND redirect, shaped
 * { locationRef, source, destination }. Two concurrent denials (e.g.
 * /Project/GetProject racing /Project/GetProjectInfo on a foreign deep link) both
 * reach the response interceptor before the first `window.location.href` assignment
 * unloads the page, and a second assignment would tear down the already-starting
 * redirect document load. The record suppresses only a re-assignment for that SAME
 * transition: a real browser keeps reporting the transition's SOURCE href until the
 * new document commits, while a location double that applies the assignment
 * synchronously reports its DESTINATION. It is bounded state, never an
 * unconditional forever flag — a later denial from a CHANGED href (any SPA
 * path/context change) or from a NEW page (a full page load both recreates this
 * module with fresh state and mints a new window.location) no longer matches the
 * recorded transition and redirects again.
 */
let pendingProjectNotFoundRedirect;

/**
 * A request left over from a REPLACED session? The request interceptor stamps every
 * request with the logical session generation it was dispatched under (config.authSession
 * — frontend-only metadata carrying ONLY that number, never credentials); the response
 * compares it with the CURRENT generation (Auth.sessionGeneration — advanced
 * once per real login/authorize/SSO-replacement/logout transition, never by token
 * rotation). Requests without a stamp (tests, manually built configs) and snapshots
 * without a generation never count as stale — they keep the legacy behavior (refresh
 * with the current credentials).
 */
const isStaleAuthRequest = (requestSession, currentAuthData) =>
	Number.isInteger(requestSession && requestSession.sessionGeneration) &&
	Number.isInteger(currentAuthData && currentAuthData.sessionGeneration) &&
	requestSession.sessionGeneration !== currentAuthData.sessionGeneration;

// Add a request interceptor
axios.interceptors.request.use(
	config => {
		// Do something before request is sent

		const authData = Auth.fillAndGetData();
		const selecteLanguage = LanguageService.getSelectedLanguage();

		const companyId = Browser.getURLParamValue("cid");
		const projectId = Browser.getURLParamValue("p");

		if (companyId) {
			// Set selected company id
			config.headers.CompanyId = companyId;
		}
		if (projectId) {
			// Set selected company id
			config.headers.ProjectId = projectId;
		}

		if (config.headers.grant_type !== "refresh_token") {
			// Set access token
			if (authData && authData.token) {
				config.headers.Authorization = `Bearer ${authData.token}`;
			}

			// Set selected language
			if (selecteLanguage) {
				config.headers["accept-language"] = selecteLanguage;
			}

			config.headers.TimeZone = Utils.getTimeZone();
			// config.headers['TimeZone'] = Utils.getTimeZone() + '.0';

			// Set default content type
			if (!config.headers["Content-Type"] || config.headers["Content-Type"] === "text/plain;charset=UTF-8") {
				config.headers["Content-Type"] = "application/json";
			}
		} else {
			delete config.headers.grant_type;
		}

		// Frontend-only request metadata (never headers/body): the LOGICAL SESSION
		// GENERATION this request belongs to, captured as an immutable scalar value before
		// the request leaves. The response interceptor compares it with the CURRENT session
		// generation so a LATE failure of a request left over from a replaced session
		// (logout -> new login / authorize / SSO storage replacement) can never refresh,
		// invalidate or reload the NEW session's credentials.
		//
		// PRIVACY: the snapshot deliberately carries ONLY the generation number — no
		// token/refreshToken fields. The installed Axios XHR adapter echoes the request
		// config on error.response.config and AxiosError.toJSON serializes that config,
		// and this module's own error logging prints error.response, so raw credentials
		// in config metadata would surface through those existing boundaries. Credential
		// material travels ONLY where the wire/backend requires it: the Authorization
		// header and the refresh POST's own form body.
		config.authSession = {
			sessionGeneration: authData.sessionGeneration,
		};

		return config;
	},
	error =>
		// Do something with request error
		Promise.reject(error),
);

// Add a response interceptor
axios.interceptors.response.use(
	response =>
		// Any status code that lie within the range of 2xx cause this function to trigger
		// Do something with response data
		response,
	error => {
		console.log(error.response);
		// Any status codes that falls outside the range of 2xx cause this function to trigger
		// Do something with response error
		const authData = Auth.fillAndGetData();
		if (
			error &&
			error.response &&
			error.response.status === ErrorType.AUTH_ERROR &&
			!error.config?.authRefreshRequest
		) {
			// The refresh POST's OWN failure is owned by refreshToken's catch (single
			// invalidation/reload there) — never refresh-on-the-refresh.
			if (!isStaleAuthRequest(error?.config?.authSession, authData)) {
				if (authData && authData.refreshToken) {
					const originalRequest = error?.config;
					return refreshToken(authData, originalRequest).catch(settledError => {
						if (settledError && settledError.isStaleAuthContext) {
							// The refresh settled STALE: the request itself DID fail with this
							// 401 under a session that has since been replaced — surface the
							// request's OWN failure (its response props are the genuine error the
							// existing error mechanics already understand), explicitly marked.
							// Never a fabricated success, never an unsettled promise.
							error.isStaleAuthContext = true;
							return Promise.reject(error);
						}
						return Promise.reject(settledError);
					});
				}
				if (getAppPathname() !== RoutePath.SSO) {
					Auth.invalidateSession();
					window.location.reload();
				}
			} else {
				// LATE 401 of a request dispatched under a REPLACED session: reject with the
				// request's own failure, explicitly marked — never refresh, never invalidate
				// or reload the NEW session's credentials.
				error.isStaleAuthContext = true;
			}
		} else if (error && error.response && error.response.status >= 400) {
			const result = error.response && error.response.data;
			if (
				result &&
				result.code === ErrorType.PROJECT_NOT_FOUND &&
				error.config?.suppressProjectNotFoundRedirect !== true
			) {
				const redirectDestination = getAppUrl(RolesHelper.getRedirectPage());
				// A redirect is already pending for this page when the SAME location
				// object is still at the recorded transition's source href (a real
				// browser keeps reporting the old URL until the redirect document
				// commits) or at its destination (a location double that applies the
				// assignment synchronously). Any other href — or a new page — is a
				// new denial and redirects again (see pendingProjectNotFoundRedirect).
				const pendingRedirect = pendingProjectNotFoundRedirect;
				const redirectPending =
					pendingRedirect &&
					pendingRedirect.locationRef === window.location &&
					(window.location.href === pendingRedirect.source || window.location.href === pendingRedirect.destination);
				if (history.location.pathname !== redirectDestination && !redirectPending) {
					// Mark BEFORE the assignment so a synchronous second denial in
					// the same tick is already guarded.
					pendingProjectNotFoundRedirect = {
						locationRef: window.location,
						source: window.location.href,
						destination: redirectDestination,
					};
					window.location.href = redirectDestination;
				}
			}
			if (!CustomErrors[result.code]) {
				// Handled error on backend side
				if (result && result.code && result.message) {
					// Wrong autg token
					if (result.code === ErrorType.WRONG_AUTH_TOKEN && !isStaleAuthRequest(error?.config?.authSession, authData)) {
						// A LATE wrong-token failure of a request from a REPLACED session must
						// not invalidate or reload the NEW session (the error is still reported
						// below — only the session teardown is session-owned).
						Auth.invalidateSession();
						window.location.reload();
					}

					let description = "";
					if (result.errors && result.errors.length > 0) {
						description = result.errors.map(elm => <p key={elm.code.toString()}>{elm.message}</p>);
					}

					notification.error({
						key: "networkError",
						placement: "bottomRight",
						duration: 5,
						// AntD 6 notification API: the visible headline is `title` (the legacy
						// `message` config key is deprecated); the backend's result.message is
						// the SOURCED input and stays unchanged, as does the description.
						title: result.message,
						description,
					});
				} else {
					// Case : unhandled error on backend side
					// eslint-disable-next-line no-lonely-if
					if (
						result.error &&
						result.error === "invalid_grant" &&
						getAppPathname() !== RoutePath.SSO &&
						!isStaleAuthRequest(error?.config?.authSession, authData) &&
						!error.config?.authRefreshRequest
					) {
						// Stale (a late old-request failure must not touch the newer session) or
						// the refresh POST itself (refreshToken's catch owns the SINGLE
						// invalidation/reload) — both skip the teardown here; the error is
						// still reported through the notification below.
						Auth.invalidateSession();
						window.location.reload();
					} else if (!error.config?.authRefreshRequest) {
						const selectednguage = LanguageService.getSelectedLanguage();
						const translation = LanguageService.getTranslation(selectednguage);

						notification.error({
							key: "generalError",
							placement: "bottomRight",
							duration: 5,
							// AntD 6 notification API: the visible headline is `title` (the
							// legacy `message` config key is deprecated); the same translation
							// strings and description are rendered unchanged.
							title: translation.messages.general_error_msg_title,
							description: translation.messages.general_error_msg_description,
						});
					}
				}
			}
		}
		return Promise.reject(error);
	},
);
